import React from 'react';

import { Box, Stack, Tooltip, Typography } from '@mui/material';

import { formatCoverageLabel, getCoveragePercentage, getStatusLabel } from './translationOverviewUtils';
import type { TranslationOverviewCounts, TranslationOverviewStatus } from './types';

interface Props {
  counts: TranslationOverviewCounts;
  total: number;
}

const SEGMENTS: Array<{ status: TranslationOverviewStatus; key: keyof TranslationOverviewCounts; color: string }> = [
  { status: 'Ready', key: 'readyCount', color: 'success.main' },
  { status: 'Pending', key: 'pendingCount', color: 'warning.main' },
  { status: 'Failed', key: 'failedCount', color: 'error.main' },
  { status: 'RequiresTranslation', key: 'requiresTranslationCount', color: 'grey.400' },
];

/**
 * Stacked progress bar summarising every pair in a row at a glance: how much is translated,
 * how much is in flight, how much failed, and how much has not been started.
 */
export default function CoverageCell({ counts, total }: Props) {
  const percentage = getCoveragePercentage(counts.readyCount, total);

  const tooltip = (
    <Stack spacing={0.25}>
      {SEGMENTS.map((segment) => (
        <Typography key={segment.status} variant="caption">
          {getStatusLabel(segment.status)}: {counts[segment.key].toLocaleString()}
        </Typography>
      ))}
    </Stack>
  );

  return (
    <Tooltip title={tooltip} placement="top" arrow>
      <Stack spacing={0.5} width="100%" justifyContent="center">
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography variant="body2" fontWeight={600}>
            {percentage}%
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatCoverageLabel(counts.readyCount, total)} ready
          </Typography>
        </Stack>

        <Stack direction="row" sx={{ height: 6, borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.200' }}>
          {SEGMENTS.map((segment) => {
            const value = counts[segment.key];
            if (!value || total <= 0) {
              return null;
            }

            return <Box key={segment.status} sx={{ flexGrow: value / total, bgcolor: segment.color }} />;
          })}
        </Stack>
      </Stack>
    </Tooltip>
  );
}
